import {
  useId,
  useLayoutEffect,
  useRef,
  type KeyboardEvent as ReactKeyboardEvent,
  type MutableRefObject,
} from 'react';
import { MentionEditable, type MentionEditableHandle } from './MentionEditable';
import { listNewline } from '../list-autoformat';

/**
 * A PLAIN multiline text box that formats a list as you type — the card's "What is
 * this about?" and description boxes.
 *
 * It is the composer's contenteditable surface (MentionEditable) without the parts
 * only the feed needs: no @mention picker, no "Enter sends". So a wrapped list item
 * hangs its second line under its first letter the way a word processor does, and a
 * bullet or number keeps itself going — exactly as in the composer — in a box that
 * is otherwise an ordinary text field. These boxes carry no @mentions, so the tint
 * is a no-op; the one renderer still gives them the same wrapping and caret.
 *
 * Enter is where a list keeps going (these fields do not send on Enter); it writes
 * the next marker, or ends the list on an empty item, and otherwise makes a plain
 * newline. Shift+Enter is always a plain newline, to break out of a list by hand.
 * Both are handled in the value — the contenteditable's own Enter would split the
 * blocks the editor manages. Everything else (Escape, the modal's own keys) bubbles.
 */
export type { MentionEditableHandle as ListEditableHandle };

export function ListEditable({
  value,
  onChange,
  className,
  placeholder = '',
  disabled,
  autoFocus,
  ariaLabel,
  handleRef,
}: {
  value: string;
  onChange: (v: string) => void;
  className: string;
  placeholder?: string;
  disabled?: boolean;
  autoFocus?: boolean;
  ariaLabel?: string;
  handleRef?: MutableRefObject<MentionEditableHandle | null>;
}) {
  const caretRef = useRef<number | null>(null);
  const edRef = useRef<MentionEditableHandle | null>(null);
  const listId = useId();

  // Mirror the editor's handle out to the caller's ref (the summary nudge focuses
  // and scrolls to the box through it). MentionEditable fills edRef in its own mount
  // effect, which runs before this parent effect, so it is set by the time we copy.
  useLayoutEffect(() => {
    if (!handleRef) return;
    handleRef.current = edRef.current;
    return () => {
      handleRef.current = null;
    };
  }, [handleRef]);

  const onKeyDown = (e: ReactKeyboardEvent<HTMLDivElement>) => {
    if (e.key !== 'Enter') return;
    // ⌘/Ctrl+Enter is the card's save chord, owned by the modal's own key handler.
    // Let it bubble untouched — otherwise this box would insert a newline (or the
    // next list marker) a tick before the save reads the value, leaving a stray
    // line behind. Mirrors MentionInput, which checks the modifier first.
    if (e.metaKey || e.ctrlKey) return;
    e.preventDefault();
    // Read the text and the caret from the SAME place — the live DOM. The `value`
    // prop can trail a tick behind the DOM right after a keystroke, and judging a
    // newline on prop-text with a DOM-caret makes a just-typed "1. item" fail to
    // continue (the caret is past the prop's end, so it reads as not at line end).
    const live = edRef.current?.value() ?? value;
    const sel = edRef.current?.selection() ?? { start: live.length, end: live.length };
    // Enter (no Shift) on a collapsed caret carries a list on, or ends it.
    if (!e.shiftKey && sel.start === sel.end) {
      const edit = listNewline(live, sel.start, sel.end);
      if (edit) {
        caretRef.current = edit.caret;
        onChange(edit.value);
        return;
      }
    }
    // Otherwise a plain newline (Shift+Enter, a non-list line, or replacing a selection).
    const next = live.slice(0, sel.start) + '\n' + live.slice(sel.end);
    caretRef.current = sel.start + 1;
    onChange(next);
  };

  return (
    <MentionEditable
      value={value}
      tint={(line) => [{ text: line }]}
      placeholder={placeholder}
      className={className}
      disabled={disabled}
      autoFocus={autoFocus}
      ariaLabel={ariaLabel}
      listId={listId}
      open={false}
      matchesLen={0}
      activeIdx={0}
      caretRef={caretRef}
      onInput={(v) => onChange(v)}
      onCaret={() => {}}
      onKeyDown={onKeyDown}
      handleRef={edRef}
    />
  );
}
